import { useAnalytics } from "@/analytics";
import { usePartner } from "@/sdk";

import { getTimeRemaining } from "./utils";

const SESSION_SUMMARY_STATS_EVENT = "SessionSummary_Stats";
const SESSION_SUMMARY_SUBMIT_EVENT = "SessionSummary_Submit";

interface SessionSummaryStatsOptionsPayload {
    agentCallSid: string | undefined;
    autogeneratedSummary?: string;
    update?: string;
    summaryWasSubmittedViaPartnerTool?: boolean;
    summaryWasCopiedToClipboard?: boolean;
    timeRanOut?: boolean;
    expirationTimestamp?: number | undefined;
    gaiaSummaryModel?: string;
    hasValidMdn?: boolean;
}

interface SessionSubmitOptionsPayload {
    agentCallSid: string | undefined;
    mdn: string;
    mdnHashed: string;
    sessionSummaryPayload: Record<string, unknown>;
}

export const useSessionSummaryDispatchActions = () => {
    const { dispatcher } = useAnalytics();
    const partner = usePartner();

    const dispatchSessionSubmit = ({
        agentCallSid,
        mdn,
        mdnHashed,
        sessionSummaryPayload,
    }: SessionSubmitOptionsPayload) => {
        void dispatcher
            .withIdentities({ CallSid: agentCallSid, mdn, HashedMdn: mdnHashed })
            .withMeta({ Client: partner, Channel: "voice", SkipMixpanel: true, SkipEDP: false, SkipOpenSearch: true })
            .dispatchBusinessEvent(SESSION_SUMMARY_SUBMIT_EVENT, sessionSummaryPayload);
    };

    const dispatchSessionSummaryStats = ({
        agentCallSid,
        autogeneratedSummary,
        update,
        summaryWasCopiedToClipboard,
        summaryWasSubmittedViaPartnerTool,
        timeRanOut,
        expirationTimestamp,
        gaiaSummaryModel,
        hasValidMdn,
    }: SessionSummaryStatsOptionsPayload) => {
        const timeRemainingInMs = getTimeRemaining(expirationTimestamp);
        void dispatcher
            .withIdentities({ CallSid: agentCallSid })
            .withMeta({ Client: partner, Channel: "voice", SkipEDP: false })
            .dispatchBusinessEvent(SESSION_SUMMARY_STATS_EVENT, {
                summaryWasAutoGenerated: autogeneratedSummary !== "",
                summaryWasEdited: update !== autogeneratedSummary,
                summaryWasSubmittedViaPartnerTool: summaryWasSubmittedViaPartnerTool ?? false,
                summaryWasCopiedToClipboard: summaryWasCopiedToClipboard ?? false,
                summaryLength: update?.length ?? 0,
                timeRanOut: timeRanOut ?? timeRemainingInMs < 1,
                timeRemainingInMs,
                gaiaSummaryModel,
                hasValidMdn,
            });
    };

    return { dispatchSessionSummaryStats, dispatchSessionSubmit };
};
